// Pure DeckModel outline mutations for the manual builder.
//
// The create page holds a DeckModel in component state and edits it through
// these helpers. They are deliberately PURE — each returns a NEW DeckModel (and
// new nested arrays/objects along the editing path) rather than mutating the
// argument in place. That keeps Lit change-detection honest (a new top-level
// reference always triggers a re-render) and makes the mutations trivially
// unit-testable without a DOM.
//
// British spelling throughout. No emojis.

import type {
  Block,
  BlockType,
  DeckModel,
  DeckMeta,
  Slide,
  SlideVariant,
} from './deck-model';
import type { SlideTemplate } from './slide-template';

/** A brand-new, empty deck the builder starts from. One cover slide so the
 *  preview is never blank and the user has something to edit immediately. */
export function emptyDeck(): DeckModel {
  return {
    meta: { title: 'Untitled deck' },
    slides: [
      {
        variant: 'cover',
        label: '01 Cover',
        blocks: [{ type: 'title', title: 'Untitled deck' }],
      },
    ],
  };
}

/** A fresh, sensible block of the given type. Used when the user clicks
 *  "Add block". Every field the renderer reads is seeded so the preview has
 *  something to show before the user types. */
export function newBlock(type: BlockType): Block {
  switch (type) {
    case 'title':
      return { type: 'title', title: 'New title' };
    case 'heading':
      return { type: 'heading', level: 2, text: 'New heading' };
    case 'paragraph':
      return { type: 'paragraph', text: 'New paragraph.' };
    case 'lede':
      return { type: 'lede', text: 'New lede.' };
    case 'bullets':
      return { type: 'bullets', items: ['First point', 'Second point'] };
    case 'cards':
      return {
        type: 'cards',
        columns: 2,
        cards: [
          { title: 'Card one', desc: 'Description' },
          { title: 'Card two', desc: 'Description' },
        ],
      };
    case 'stat-row':
      return {
        type: 'stat-row',
        stats: [
          { n: '100', label: 'Metric' },
          { n: '200', label: 'Metric' },
        ],
      };
    case 'quote':
      return { type: 'quote', text: 'A pull-quote.', attribution: 'Attribution' };
    case 'table':
      return {
        type: 'table',
        headers: ['Column A', 'Column B'],
        rows: [['Cell', 'Cell']],
      };
    case 'kv':
      return { type: 'kv', items: [{ k: 'Key', v: 'Value' }] };
    case 'rule':
      return { type: 'rule' };
    case 'raw-html':
      return { type: 'raw-html', html: '<p class="body">Raw HTML</p>' };
    // ── Wave 3 ──
    case 'media-split':
      return {
        type: 'media-split',
        image: { src: '/img/placeholder.png', alt: 'Image' },
        blocks: [{ type: 'paragraph', text: 'Content beside the image.' }],
      };
    case 'media-full':
      return {
        type: 'media-full',
        image: { src: '/img/placeholder.png', alt: 'Background' },
        eyebrow: 'Eyebrow',
        title: 'Full-bleed title',
      };
    case 'media-grid':
      return {
        type: 'media-grid',
        images: [
          { src: '/img/placeholder.png', alt: 'Tile one' },
          { src: '/img/placeholder.png', alt: 'Tile two' },
        ],
      };
    case 'figure':
      return {
        type: 'figure',
        image: { src: '/img/placeholder.png', alt: 'Figure' },
        caption: 'Figure caption',
      };
    case 'bignum':
      return {
        type: 'bignum',
        value: '100',
        label: 'Metric',
        delta: { dir: 'up', text: '+10%' },
      };
    case 'testimonial':
      return {
        type: 'testimonial',
        quote: 'A glowing testimonial.',
        name: 'Name',
        role: 'Role',
        company: 'Company',
      };
    case 'chart-bars':
      return {
        type: 'chart-bars',
        bars: [
          { label: 'A', value: 30 },
          { label: 'B', value: 60 },
          { label: 'C', value: 45 },
        ],
      };
    case 'donut':
      return {
        type: 'donut',
        value: 72,
        label: 'Adoption',
        segments: [
          { label: 'Yes', value: 72 },
          { label: 'No', value: 28 },
        ],
      };
    case 'chart-line':
      return {
        type: 'chart-line',
        series: [{ label: 'Series', points: [10, 25, 18, 40, 32] }],
      };
    case 'agenda':
      return {
        type: 'agenda',
        items: [
          { text: 'Introduction' },
          { text: 'Main content', active: true },
          { text: 'Conclusion' },
        ],
      };
    case 'compare':
      return {
        type: 'compare',
        headers: ['Us', 'Them'],
        rows: [
          { label: 'Feature one', cells: ['yes', 'no'] },
          { label: 'Feature two', cells: ['yes', 'partial'] },
        ],
      };
    case 'proscons':
      return {
        type: 'proscons',
        pros: ['First advantage', 'Second advantage'],
        cons: ['First drawback'],
      };
    case 'feature-card':
      return {
        type: 'feature-card',
        title: 'Feature',
        desc: 'What this feature does.',
      };
    case 'icon-list':
      return {
        type: 'icon-list',
        items: [
          { text: 'First item' },
          { text: 'Second item' },
        ],
      };
    case 'cta':
      return {
        type: 'cta',
        title: 'Get in touch',
        sub: 'We would love to talk.',
        contact: [
          { k: 'Email', v: 'hello@example.com' },
          { k: 'Web', v: 'example.com' },
        ],
      };
    // ── Drift-closing ──
    case 'apps-hub':
      return {
        type: 'apps-hub',
        cols: 3,
        tiles: [
          { eyebrow: 'Category', name: 'App one', url: 'app.example.com', note: 'One-line description.' },
          { eyebrow: 'Category', name: 'App two', url: 'app2.example.com', note: 'One-line description.' },
          { eyebrow: 'Category', name: 'App three', url: 'app3.example.com', note: 'One-line description.' },
        ],
      };
    case 'tier-cap':
      return {
        type: 'tier-cap',
        caps: [
          { name: 'Essentials', what: 'Asset visualisation + booking' },
          { name: 'Pro', what: '+ monitoring + alerts', featured: true },
          { name: 'Intelligence', what: '+ Workplace AI + predictive', intel: true },
        ],
      };
    case 'region-grid':
      return {
        type: 'region-grid',
        regions: [
          {
            label: 'Region',
            items: [
              { feedback: 'A raised concern.', response: 'Our response.' },
            ],
          },
        ],
      };
    case 'signal-flow':
      return {
        type: 'signal-flow',
        sources: [
          { icon: 'info', name: 'Source one', sub: 'detail' },
          { icon: 'warning', name: 'Source two' },
        ],
        engine: { name: 'Ingestion engine', sub: 'One ingestion' },
        surfaces: [
          { name: 'Surface one', eg: 'Example use.' },
          { name: 'Surface two' },
        ],
      };
    case 'floorplan':
      return {
        type: 'floorplan',
        rooms: [
          { label: 'Room', x: 100, y: 120, w: 120, h: 70 },
          { label: 'Room', x: 260, y: 120, w: 120, h: 70 },
        ],
        pins: [
          { app: 'emp', x: 160, y: 155, label: 'Employee app', lead: 'Room availability' },
          { app: 'man', x: 320, y: 155, label: 'Manage' },
        ],
        kpis: [
          { n: '46,178', label: 'Spaces' },
          { n: '560', label: 'Locations' },
        ],
      };
    case 'layer-band':
      return {
        type: 'layer-band',
        bands: [
          { name: 'Workplace AI', desc: 'Predicts, automates, summarises.', tint: 'intel' },
          { name: 'Operations Layer', desc: 'Devices, alerts, issues, vendors.', tint: 'ops' },
          { name: 'Spatial Foundation', desc: 'Buildings, floors, spaces, assets.', tint: 'spatial' },
        ],
      };
    case 'ai-stack':
      return {
        type: 'ai-stack',
        tiers: [
          {
            cards: [
              { eyebrow: '01 · Conversational', title: 'Ask Workplace', desc: 'Natural-language search.', featured: true },
            ],
          },
          {
            cards: [
              { eyebrow: 'Help me improve', title: 'Setup hygiene', desc: 'In-app suggestions.', loc: 'Employee app · Admin', status: 'live' },
              { eyebrow: 'Planned', title: 'Predictive', desc: 'Forecasts demand.', status: 'planned' },
            ],
          },
        ],
      };
    case 'engine-grid':
      return {
        type: 'engine-grid',
        engines: [
          {
            eyebrow: 'One ingestion',
            title: 'Unified asset graph',
            code: 'rdx-platform-api',
            desc: 'One asset model, one alert engine, one vendor adapter.',
            quote: 'Ten vendor APIs flatten into one shape.',
          },
        ],
      };
    case 'cycle':
      return {
        type: 'cycle',
        points: [
          { label: '14-day window', desc: 'Mon to Sun. One demo at the close.' },
          { label: 'Demo gate', desc: 'Every cycle ends with a live demo.' },
          { label: 'Continuity', desc: 'Carry-forward into the next cycle.' },
        ],
      };
    case 'roadmap':
      return {
        type: 'roadmap',
        milestones: [
          { when: '2024', label: 'Milestone one' },
          { when: '2025', label: 'Milestone two' },
          { when: '2026', label: 'Milestone three', future: true },
        ],
      };
    case 'bar-h':
      return {
        type: 'bar-h',
        bars: [
          { label: 'Metric one', value: 9, tone: 'ok' },
          { label: 'Metric two', value: 2, tone: 'warn' },
        ],
        max: 11,
      };
    case 'pricing-tbl':
      return {
        type: 'pricing-tbl',
        columns: [
          { label: 'SKU' },
          { label: 'Essentials' },
          { label: 'Pro' },
          { label: 'Intelligence', intel: true },
          { label: 'Unit' },
        ],
        rows: [
          { cells: ['Rooms', '€120', '€170', { value: '€260', intel: true }, 'per room / yr'] },
          { single: true, cells: ['Single-tier asset SKUs · Print Monitor €12 · Connected Printer €45'] },
        ],
      };
    // ── TIER-C ──
    case 'callout-banner':
      return {
        type: 'callout-banner',
        eyebrow: 'The shift',
        text: 'The conversation flips.',
        accent: 'red',
      };
    case 'equation':
      return {
        type: 'equation',
        terms: ['Spatial', 'Workplace', 'Intelligence'],
        result: 'Workplace OS',
      };
    case 'chip-wall':
      return {
        type: 'chip-wall',
        title: 'Category wall',
        chips: [
          { label: 'Item one', category: 'info' },
          { label: 'Item two', category: 'success' },
          { label: 'Item three', category: 'warning' },
        ],
        legend: [
          { label: 'Type A', category: 'info' },
          { label: 'Type B', category: 'success' },
        ],
      };
    case 'command-hub':
      return {
        type: 'command-hub',
        label: 'Ask Workplace anything',
        hint: 'Press Cmd-K',
        items: ['Book a room', 'Find a colleague', 'Raise an issue'],
      };
    case 'before-after':
      return {
        type: 'before-after',
        before: { title: 'Today', items: ['Five tools', 'Five logins', 'No single view'] },
        after: { title: 'Workplace by Ricoh', items: ['One platform', 'One login', 'One view'] },
      };
    // ── V5 ──
    case 'split':
      return {
        type: 'split',
        ratio: '1-1',
        left: [{ type: 'paragraph', text: 'Left column.' }],
        right: [{ type: 'paragraph', text: 'Right column.' }],
      };
    case 'panel':
      return {
        type: 'panel',
        label: 'Label',
        title: 'Panel title',
        blocks: [{ type: 'paragraph', text: 'Panel body.' }],
      };
    case 'timeline':
      return {
        type: 'timeline',
        ticks: ['2024', '2025', '2026'],
        markers: [
          { when: 'Milestone one', label: 'First' },
          { when: 'Milestone two', label: 'Second', future: true },
        ],
      };
    case 'feature-grid':
      return {
        type: 'feature-grid',
        columns: 3,
        items: [
          { eyebrow: 'One', title: 'Feature one', desc: 'What it does.' },
          { eyebrow: 'Two', title: 'Feature two', desc: 'What it does.' },
          { eyebrow: 'Three', title: 'Feature three', desc: 'What it does.' },
        ],
      };
    case 'progress':
      return {
        type: 'progress',
        items: [
          { label: 'Metric one', value: 9, total: 11 },
          { label: 'Metric two', value: 80 },
        ],
      };
    default: {
      // Exhaustiveness guard — a new BlockType without a case fails to compile.
      const _exhaustive: never = type;
      return _exhaustive;
    }
  }
}

/** A fresh content slide with a single title block. */
export function newSlide(index: number): Slide {
  const n = String(index + 1).padStart(2, '0');
  return {
    variant: 'content',
    label: `${n} Slide`,
    blocks: [{ type: 'title', title: 'New slide' }],
  };
}

/** Replace the deck meta wholesale (the editor binds individual fields and
 *  passes a merged object). */
export function updateMeta(model: DeckModel, meta: Partial<DeckMeta>): DeckModel {
  return { ...model, meta: { ...model.meta, ...meta } };
}

/** Append a fresh content slide. */
export function addSlide(model: DeckModel): DeckModel {
  const slides = [...model.slides, newSlide(model.slides.length)];
  return { ...model, slides };
}

/** Remove the slide at `index`. A deck must keep at least one slide, so a
 *  remove that would empty the deck is a no-op (the UI also disables the
 *  control in that state, but the helper stays safe on its own). */
export function removeSlide(model: DeckModel, index: number): DeckModel {
  if (index < 0 || index >= model.slides.length) return model;
  if (model.slides.length <= 1) return model;
  const slides = model.slides.filter((_, i) => i !== index);
  return { ...model, slides };
}

/** Move the slide at `index` by `delta` (+1 down, -1 up). Out-of-range moves
 *  are no-ops so the caller can wire the buttons without bounds-checking. */
export function moveSlide(model: DeckModel, index: number, delta: number): DeckModel {
  const target = index + delta;
  if (index < 0 || index >= model.slides.length) return model;
  if (target < 0 || target >= model.slides.length) return model;
  const slides = [...model.slides];
  const [moved] = slides.splice(index, 1);
  slides.splice(target, 0, moved);
  return { ...model, slides };
}

/**
 * Insert a reusable slide template into the deck at `index`.
 *
 * The template's canonical `slide` is DEEP-CLONED (structuredClone) before it is
 * spliced in, so the frozen catalogue slide is never aliased into the editable
 * model — later block/slide edits on the inserted copy can never mutate the
 * shared builtin. `index` is clamped to [0, slides.length]: a negative index
 * inserts at the start, an over-length index appends. Returns a NEW DeckModel
 * with new nested arrays along the editing path, like every other helper here.
 */
export function insertSlideTemplate(
  model: DeckModel,
  index: number,
  tpl: SlideTemplate,
): DeckModel {
  const at = Math.max(0, Math.min(index, model.slides.length));
  const clone = structuredClone(tpl.slide);
  const slides = [...model.slides];
  slides.splice(at, 0, clone);
  return { ...model, slides };
}

/** Set the variant of the slide at `index`. */
export function setSlideVariant(
  model: DeckModel,
  index: number,
  variant: SlideVariant,
): DeckModel {
  if (index < 0 || index >= model.slides.length) return model;
  const slides = model.slides.map((s, i) => (i === index ? { ...s, variant } : s));
  return { ...model, slides };
}

/** Append a fresh block of `type` to the slide at `slideIndex`. */
export function addBlock(model: DeckModel, slideIndex: number, type: BlockType): DeckModel {
  if (slideIndex < 0 || slideIndex >= model.slides.length) return model;
  const slides = model.slides.map((s, i) =>
    i === slideIndex ? { ...s, blocks: [...s.blocks, newBlock(type)] } : s,
  );
  return { ...model, slides };
}

/** Remove the block at `blockIndex` from the slide at `slideIndex`. */
export function removeBlock(
  model: DeckModel,
  slideIndex: number,
  blockIndex: number,
): DeckModel {
  if (slideIndex < 0 || slideIndex >= model.slides.length) return model;
  const slides = model.slides.map((s, i) => {
    if (i !== slideIndex) return s;
    return { ...s, blocks: s.blocks.filter((_, j) => j !== blockIndex) };
  });
  return { ...model, slides };
}

/** Move the block at `blockIndex` within its slide by `delta`. No-op out of
 *  range. */
export function moveBlock(
  model: DeckModel,
  slideIndex: number,
  blockIndex: number,
  delta: number,
): DeckModel {
  if (slideIndex < 0 || slideIndex >= model.slides.length) return model;
  const slide = model.slides[slideIndex];
  const target = blockIndex + delta;
  if (blockIndex < 0 || blockIndex >= slide.blocks.length) return model;
  if (target < 0 || target >= slide.blocks.length) return model;
  const blocks = [...slide.blocks];
  const [moved] = blocks.splice(blockIndex, 1);
  blocks.splice(target, 0, moved);
  const slides = model.slides.map((s, i) => (i === slideIndex ? { ...s, blocks } : s));
  return { ...model, slides };
}

/**
 * Patch the block at `[slideIndex][blockIndex]` with the given partial. The
 * patch is shallow-merged onto the existing block, so callers pass only the
 * fields that changed (e.g. `{ text: '...' }` or `{ items: [...] }`). The
 * block's `type` discriminant is preserved — a patch may not change it; use
 * removeBlock + addBlock to swap a block's type instead.
 *
 * Typed loosely as `Partial<Block>` for ergonomics at the call sites; the
 * builder only ever passes fields that belong to the target block's variant.
 */
export function updateBlock(
  model: DeckModel,
  slideIndex: number,
  blockIndex: number,
  patch: Partial<Block>,
): DeckModel {
  if (slideIndex < 0 || slideIndex >= model.slides.length) return model;
  const slides = model.slides.map((s, i) => {
    if (i !== slideIndex) return s;
    const blocks = s.blocks.map((b, j) => {
      if (j !== blockIndex) return b;
      // Preserve the discriminant; spread the patch over the block. Cast is
      // safe because the builder never passes a patch with a different `type`.
      return { ...b, ...patch, type: b.type } as Block;
    });
    return { ...s, blocks };
  });
  return { ...model, slides };
}
